import { supabase } from '../lib/supabase';
import { SportsYear, SportsYearStatus } from '../types/models';

export const getSportsYears = async (): Promise<SportsYear[]> => {
  const { data, error } = await supabase
    .from('sports_years')
    .select('*')
    .order('year', { ascending: false });

  if (error) throw new Error(error.message);
  return data || [];
};

export const getActiveSportsYear = async (): Promise<number> => {
  const { data, error } = await supabase
    .from('sports_years')
    .select('year')
    .eq('status', 'active')
    .maybeSingle();

  if (error) throw new Error(error.message);
  return data?.year ?? new Date().getFullYear();
};

export const createSportsYear = async (input: {
  year: number;
  label?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  status?: SportsYearStatus;
}): Promise<SportsYear> => {
  const payload = {
    year: input.year,
    label: input.label ?? input.year.toString(),
    start_date: input.start_date ?? null,
    end_date: input.end_date ?? null,
    status: input.status ?? 'planning',
  };

  if (payload.status === 'active') {
    await archiveActiveYears();
  }

  const { data, error } = await supabase
    .from('sports_years')
    .insert([payload])
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
};

export const updateSportsYear = async (
  yearId: string,
  patch: Partial<Omit<SportsYear, 'year_id' | 'created_at' | 'updated_at'>>
): Promise<SportsYear> => {
  if (patch.status === 'active') {
    const { data: target, error: targetError } = await supabase
      .from('sports_years')
      .select('year_id')
      .eq('year_id', yearId)
      .single();
    if (targetError) throw new Error(targetError.message);
    await archiveActiveYears(target.year_id);
  }

  const { data, error } = await supabase
    .from('sports_years')
    .update(patch)
    .eq('year_id', yearId)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
};

export const activateSportsYear = async (year: number): Promise<void> => {
  const { error } = await supabase.rpc('set_active_sports_year', {
    target_year: year,
  });
  if (error) throw new Error(error.message);
};

export const deleteSportsYear = async (yearId: string): Promise<void> => {
  const { error } = await supabase
    .from('sports_years')
    .delete()
    .eq('year_id', yearId);

  if (error) throw new Error(error.message);
};

const archiveActiveYears = async (exceptYearId?: string): Promise<void> => {
  let query = supabase
    .from('sports_years')
    .update({ status: 'archived' })
    .eq('status', 'active');

  if (exceptYearId) {
    query = query.neq('year_id', exceptYearId);
  }

  const { error } = await query;
  if (error) throw new Error(error.message);
};
